'use client'

import { FormSubmit, useFormContext } from '@ariakit/react'
import { ComponentProps, MouseEvent, useContext } from 'react'

import { Button, ButtonSize } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../_providers/TranslationProvider.client'
import { FormSubmitContent } from '../form/FormSubmitContent'

import { useVariantSelectionPurchasingState } from './useVariantSelectionPurchasingState'
import { VariantSelectionFormContext } from './VariantSelectionForm'

type VariantSelectionFormPurchasingSubmitProps = {
  callToActionColor?: React.CSSProperties['color']
  buttonSize?: ButtonSize
} & ComponentProps<typeof Button>

export const VariantSelectionFormPurchasingSubmit = ({
  isPrimaryCtaForPageView,
  callToActionColor,
  buttonSize = 'large',
  ...buttonProps
}: VariantSelectionFormPurchasingSubmitProps) => {
  const { selectedVariant, variantGroup, loading, sizeLabelOverride } = useContext(
    VariantSelectionFormContext
  )!
  const translations = useTranslation()
  const formContext = useFormContext()
  const { disabled, allVariantFieldsFilled, notFound } = useVariantSelectionPurchasingState()
  const addToBagContent = buttonProps.children ?? translations.detailsSection.addToBagCTA
  const selectSizeContent = sizeLabelOverride
    ? translations.detailsSection.selectSizeLabelOverrideAddToBagButtonCTA.replace(
        '{{sizeLabel}}',
        sizeLabelOverride
      )
    : translations.detailsSection.selectSizeAddToBagButtonCTA
  const incompleteFormContent = isPrimaryCtaForPageView ? selectSizeContent : addToBagContent
  const waistlistButtonText =
    buttonSize !== 'large'
      ? translations.detailsSection.waitlistAddToBagCTAShort
      : translations.detailsSection.waitlistAddToBagCTA

  return (
    <FormSubmit
      render={htmlProps => (
        <>
          <Button
            disabled={disabled}
            block
            // TODO: Fix this auto-generated suppression.
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            data-color={variantGroup?.color?.id}
            data-fit={selectedVariant?.fit?.id}
            data-size={selectedVariant?.size?.id}
            size={buttonSize}
            callToActionColor={callToActionColor}
            {...htmlProps}
            {...buttonProps}
            onClick={(event: MouseEvent) => {
              // Some times people portal this button. React context passes portals, DOM form submit defaults do not.
              // Because of this, we manually submit the form.
              event.preventDefault()
              formContext?.submit()
              return false
            }}
          >
            {/* There was a request to show a loading state if we're waiting on the feature flag decision here. */}
            {/* Feels kinda gross coopting `isSubmitting`, but adding this for now since it's a test. Will revisit if the test wins. */}
            <FormSubmitContent
              isSubmitting={loading}
              submittingLabel={translations.magnolia.general.loading}
            >
              {!allVariantFieldsFilled && !disabled
                ? incompleteFormContent
                : notFound
                ? translations.detailsSection.soldOutAddToBagCTA
                : selectedVariant?.availability === 'WAIT_LISTING'
                ? waistlistButtonText
                : selectedVariant?.availability === 'AVAILABLE'
                ? addToBagContent
                : translations.detailsSection.soldOutAddToBagCTA}
            </FormSubmitContent>
          </Button>
        </>
      )}
    />
  )
}
